import { useCallback, useEffect, useRef, useState } from "react";

export interface SectionPageResult<T> {
  candidates: T[];
  total: number;
  total_pages: number;
}

/** Pagina uma seção (um status) por scroll infinito — cada seção acumula
 * suas próprias páginas e busca de forma independente das demais (ideal
 * pra colunas de Kanban, uma ao lado da outra, cada uma com seu scroll). */
export function useInfiniteSectionScroll<T>(
  fetcher: (page: number) => Promise<SectionPageResult<T>>,
  enabled = true,
) {
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  // true a partir da 1ª resposta (sucesso ou erro) — evita mostrar "sem
  // candidatos" antes da seção sequer ter sido buscada.
  const [hasFetched, setHasFetched] = useState(false);
  const pageRef = useRef(1);
  const loadingRef = useRef(false);

  const load = useCallback((pageToLoad: number, append: boolean, silent = false) => {
    if (!enabled || loadingRef.current) return;
    loadingRef.current = true;
    if (append) setIsLoadingMore(true);
    else if (!silent) setIsLoadingInitial(true);
    setError(false);
    fetcher(pageToLoad)
      .then((res) => {
        setItems((prev) => (append ? [...prev, ...res.candidates] : res.candidates));
        setTotal(res.total);
        setTotalPages(res.total_pages);
        setPage(pageToLoad);
        pageRef.current = pageToLoad;
      })
      .catch(() => setError(true))
      .finally(() => {
        loadingRef.current = false;
        setHasFetched(true);
        if (append) setIsLoadingMore(false);
        else if (!silent) setIsLoadingInitial(false);
      });
  }, [fetcher, enabled]);

  // Recomeça do zero sempre que o fetcher muda de identidade (busca/filtros)
  // ou a seção é (re)ativada — evita paginação obsoleta ao trocar filtros.
  useEffect(() => {
    pageRef.current = 1;
    setItems([]);
    setHasFetched(false);
    load(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const loadMore = useCallback(() => {
    if (loadingRef.current || pageRef.current >= totalPages) return;
    load(pageRef.current + 1, true);
  }, [load, totalPages]);

  /** Recarrega a coluna do zero (colapsa de volta pra 1ª página). Usado após
   * mutações (cancelar/reativar candidato, edição no drawer). */
  const refetch = useCallback((silent = false) => {
    pageRef.current = 1;
    load(1, false, silent);
  }, [load]);

  return {
    candidates: items,
    total,
    hasMore: page < totalPages,
    isLoadingInitial,
    isLoadingMore,
    hasFetched,
    error,
    loadMore,
    refetch,
  };
}
